import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MailCheck, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { loginSuccess } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "loading" : "error"); // loading | success | error
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        loginSuccess(data);
        setStatus("success");
        toast.success(data.message || "Adresse email confirmée !");
        setTimeout(() => navigate(data.role === "admin" ? "/admin" : "/tableau-de-bord", { replace: true }), 1200);
      } catch (err) {
        setStatus("error");
        toast.error(apiError(err.response?.data?.detail) || "Échec de la confirmation");
      }
    })();
  }, [token, loginSuccess, navigate]);

  return (
    <div className="min-h-screen bg-[#F9F6F0] flex flex-col">
      <div className="relative h-44 overflow-hidden bg-[#1B4332]">
        <div className="absolute inset-0 fabric-grain opacity-20" />
        <div className="absolute bottom-4 left-5">
          <h1 className="font-display text-2xl font-extrabold text-white">SuccèsNet</h1>
          <p className="text-white/80 text-sm">Confirmation de l'email</p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 -mt-6 bg-[#F9F6F0] rounded-t-3xl px-5 pt-10 max-w-lg w-full mx-auto text-center"
      >
        {status === "loading" && (
          <div data-testid="verify-loading">
            <div className="h-12 w-12 rounded-full border-4 border-[#E85D04]/30 border-t-[#E85D04] animate-spin mx-auto mb-4" />
            <p className="text-sm text-[#5C574F]">Confirmation de votre adresse email…</p>
          </div>
        )}
        {status === "success" && (
          <div data-testid="verify-success">
            <div className="h-16 w-16 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-4">
              <MailCheck className="h-8 w-8 text-[#2D6A4F]" />
            </div>
            <h2 className="font-display text-xl font-extrabold mb-2">Email confirmé !</h2>
            <p className="text-sm text-[#5C574F]">Votre compte est activé. Redirection vers votre tableau de bord…</p>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-error">
            <div className="h-16 w-16 rounded-full bg-[#E85D04]/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-[#E85D04]" />
            </div>
            <h2 className="font-display text-xl font-extrabold mb-2">Lien invalide ou expiré</h2>
            <p className="text-sm text-[#5C574F] mb-6">Ce lien de confirmation est incomplet, expiré ou déjà utilisé. Connectez-vous pour demander un nouveau lien.</p>
            <button
              data-testid="verify-back-login-btn"
              onClick={() => navigate("/connexion")}
              className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform"
            >
              Aller à la connexion
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
